import { KeplerVisualChannel, RasterStyleType } from 'features/builder/types'
import { CHANNEL_SCALES } from '@carto/kepler.gl/constants'

export type VisualChannelKey =
  | 'color'
  | 'strokeColor'
  | 'size'
  | 'radius'
  | 'height'
  | 'weight'
  | 'uniqueValuesColor'
  | 'customMarkers'

type VisualChannelConfig = Omit<KeplerVisualChannel, 'nullValue' | 'defaultValue'> & {
  // key in `scales` object from `getLayerDescriptor`
  // TODO: bettern name (?)
  cartoScaleKey: string
}

export const visualChannelsConfigs: Record<VisualChannelKey, VisualChannelConfig> = {
  // visual channel used by most layers for fillColor
  color: {
    cartoScaleKey: 'fillColor',

    key: 'color',
    property: 'color',
    field: 'colorField',
    scale: 'colorScale',
    domain: 'colorDomain',
    range: 'colorRange',

    aggregation: 'colorAggregation',
    channelScaleType: CHANNEL_SCALES.color,
    condition: (config) => Boolean(config.visConfig.filled)
  },

  // visual channel used by most layers for stroke/outline color
  strokeColor: {
    cartoScaleKey: 'lineColor',

    key: 'strokeColor',
    property: 'strokeColor',
    opacityProperty: 'strokeOpacity',
    field: 'strokeColorField',
    scale: 'strokeColorScale',
    domain: 'strokeColorDomain',
    range: 'strokeColorRange',

    aggregation: 'strokeColorAggregation',
    channelScaleType: CHANNEL_SCALES.color,
    condition: (config) => Boolean(config.visConfig.stroked)
  },

  // Visual channel used by most layers for stroke/outline width
  // Old document mode layers used it differently:
  //   - point used it for radius of point
  //   - grid/hex use this channel for height
  size: {
    cartoScaleKey: 'lineWidth',

    // Kepler names this channel as size, but it should be strokeWidth
    property: 'size',
    field: 'sizeField',
    scale: 'sizeScale',
    domain: 'sizeDomain',
    range: 'sizeRange',
    key: 'size',
    channelScaleType: CHANNEL_SCALES.size,
    aggregation: 'sizeAggregation',
    condition: (config) => Boolean(config.visConfig.stroked)
  },
  radius: {
    cartoScaleKey: 'pointRadius',

    // Used for point layers
    property: 'radius',
    field: 'radiusField',
    scale: 'radiusScale',
    domain: 'radiusDomain',
    range: 'radiusRange',
    key: 'radius',
    channelScaleType: CHANNEL_SCALES.radius,
    aggregation: 'radiusAggregation'
  },
  height: {
    cartoScaleKey: 'elevation',
    property: 'height',
    field: 'heightField',
    scale: 'heightScale',
    domain: 'heightDomain',
    range: 'heightRange',
    key: 'height',
    channelScaleType: CHANNEL_SCALES.size,
    aggregation: 'heightAggregation'
  },
  weight: {
    cartoScaleKey: 'weight',

    key: 'weight',
    property: 'weight',
    field: 'weightField',
    scale: 'weightScale',
    domain: 'weightDomain',
    range: 'weightRange',
    channelScaleType: CHANNEL_SCALES.size,
    aggregation: 'weightAggregation'
  },
  uniqueValuesColor: {
    cartoScaleKey: 'fillColor',

    key: 'uniqueValuesColor',
    property: 'uniqueValuesColor',
    field: 'colorField',
    scale: 'uniqueValuesColorScale',
    domain: 'uniqueValuesColorDomain',
    range: 'uniqueValuesColorRange',
    channelScaleType: 'color',
    accessor: 'getFillColor',
    condition: (layerConfig) => layerConfig.visConfig.rasterStyleType === RasterStyleType.UniqueValues
  },
  customMarkers: {
    cartoScaleKey: 'customMarkers',
    key: 'customMarkers',

    property: 'customMarkers',
    field: 'customMarkersField',
    scale: 'customMarkersScale',
    domain: 'customMarkersDomain',
    range: 'customMarkersRange',
    channelScaleType: CHANNEL_SCALES.customMarker,
    accessor: 'getIconUrl',
    condition: (config) => Boolean(config.visConfig.customMarkers)
  }
} as const
